import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Calendar, Tag, Check, Copy, Share2, BookOpen, ExternalLink } from "lucide-react";
import { ARTICLES } from "../data/mock";
import HighlightTooltip from "../components/widgets/HighlightTooltip";
import TechTag from "../components/common/TechTag";

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  const [copiedCodeId, setCopiedCodeId] = useState(null);
  const [copiedShare, setCopiedShare] = useState(false);

  if (!article) {
    return (
      <div className="pt-40 pb-28 px-6 text-center max-w-lg mx-auto">
        <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
        <h1 className="text-3xl font-medium tracking-tight mb-3">Field Note Not Found</h1>
        <p className="text-muted-foreground mb-8">
          The requested essay could not be located in the published index.
        </p>
        <Link
          to="/articles"
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase px-5 py-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Return to Field Notes
        </Link>
      </div>
    );
  }

  // Related articles (excluding current)
  const relatedArticles = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 2);

  const handleCopyCode = async (codeSnippet, codeId) => {
    try {
      await navigator.clipboard.writeText(codeSnippet);
      setCopiedCodeId(codeId);
      setTimeout(() => setCopiedCodeId(null), 2000);
    } catch {
      // Fallback
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2200);
    } catch {
      // Fallback
    }
  };

  return (
    <article className="pt-36 sm:pt-40 pb-28 px-6 sm:px-8 lg:px-12">
      <div className="max-w-[760px] mx-auto">
        
        {/* Navigation Breadcrumb & Share */}
        <div className="flex items-center justify-between mb-10 pb-6 border-b border-border/60">
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground hover:text-primary transition-colors focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Field Notes Index
          </Link>

          <button
            onClick={handleShare}
            aria-label="Copy link to this article"
            className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground px-3 py-1 rounded-full border border-border/70 hover:border-border transition-colors"
          >
            {copiedShare ? (
              <>
                <Check className="h-3 w-3 text-emerald-400" />
                <span className="text-emerald-400">Link Copied</span>
              </>
            ) : (
              <>
                <Share2 className="h-3 w-3" />
                <span>Share</span>
              </>
            )}
          </button>
        </div>

        {/* Article Header */}
        <header className="mb-12">
          <div className="flex flex-wrap items-center gap-2.5 mb-4">
            <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-primary px-3 py-0.5 rounded-full border border-primary/30 bg-primary/10">
              {article.category}
            </span>
            {article.tags?.map((tag) => (
              <span key={tag} className="font-mono text-[10px] tracking-wider text-muted-foreground/80">
                #{tag}
              </span>
            ))}
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="text-3xl sm:text-5xl md:text-6xl font-medium tracking-display leading-[1.1] text-foreground"
          >
            {article.title}
          </motion.h1>

          <p className="mt-5 text-xl sm:text-2xl text-foreground/80 font-normal leading-relaxed">
            {article.excerpt}
          </p>

          {/* Byline and metadata */}
          <div className="mt-8 pt-6 border-t border-border/50 flex flex-wrap items-center justify-between gap-4 font-mono text-[11.5px] text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="text-foreground/90 font-medium">Firza Miftahul Ilmi</span>
              <span>·</span>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {article.date}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-primary/90">
              <Clock className="h-3.5 w-3.5" />
              <span>{article.readMin} min estimated read</span>
            </div>
          </div>
        </header>

        {/* Table of Contents for Long Form */}
        {article.toc && article.toc.length > 0 && (
          <nav aria-label="Table of contents" className="mb-12 p-6 rounded-xl border border-border/70 bg-card/40 backdrop-blur-sm">
            <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-primary mb-3">
              Contents
            </p>
            <ol className="space-y-2 text-[14px]">
              {article.toc.map((item, idx) => (
                <li key={item.id} className="flex items-baseline gap-2">
                  <span className="font-mono text-[11px] text-muted-foreground/70">
                    0{idx + 1}.
                  </span>
                  <a
                    href={`#${item.id}`}
                    className="text-foreground/80 hover:text-primary transition-colors"
                  >
                    {item.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* Reading Column with Interactive Text Selection */}
        <HighlightTooltip>
          <div className="prose prose-invert max-w-none text-[17px] leading-[1.8] text-foreground/85 space-y-10">
            {article.sections ? (
              article.sections.map((sec) => (
                <section key={sec.id} id={sec.id} className="scroll-mt-28 space-y-5">
                  <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-foreground pt-4">
                    {sec.heading}
                  </h2>

                  {sec.content.map((p, pIdx) => (
                    <p key={pIdx} className="leading-relaxed">
                      {p}
                    </p>
                  ))}

                  {sec.quote && (
                    <blockquote className="my-6 pl-5 border-l-2 border-primary/70 italic text-[18px] sm:text-[19px] text-foreground/95 bg-primary/5 py-3 pr-4 rounded-r-lg">
                      &ldquo;{sec.quote}&rdquo;
                    </blockquote>
                  )}

                  {sec.code && (
                    <div className="my-6 rounded-xl border border-border/80 bg-[#060609] overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60 bg-white/[0.02]">
                        <span className="font-mono text-[11px] text-muted-foreground/80">
                          {sec.code.title || sec.code.language}
                        </span>
                        <button
                          onClick={() => handleCopyCode(sec.code.snippet, sec.id)}
                          className="inline-flex items-center gap-1 font-mono text-[10.5px] tracking-wider uppercase text-muted-foreground hover:text-foreground px-2 py-0.5 rounded transition-colors"
                        >
                          {copiedCodeId === sec.id ? (
                            <>
                              <Check className="h-3 w-3 text-emerald-400" />
                              <span className="text-emerald-400">Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy className="h-3 w-3" />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                      <pre className="p-4 sm:p-5 text-[13px] font-mono leading-relaxed text-foreground/90 overflow-x-auto whitespace-pre">
                        <code>{sec.code.snippet}</code>
                      </pre>
                    </div>
                  )}
                </section>
              ))
            ) : (
              article.body?.map((p, i) => <p key={i}>{p}</p>)
            )}

            {/* Selection Tip */}
            <div className="pt-6 border-t border-border/50">
              <p className="font-mono text-[11.5px] text-muted-foreground/75 italic">
                Tip: Highlight any phrase in this essay to copy, generate a citation quote, search the concept, or open an AI explanation.
              </p>
            </div>
          </div>
        </HighlightTooltip>

        {/* Author Bio Box */}
        <div className="mt-16 p-6 rounded-xl border border-border/70 bg-card/30 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <img
            src="/profile-photo.png"
            alt="Firza Miftahul Ilmi"
            className="h-16 w-16 rounded-full border border-border object-cover object-top bg-secondary/30 shrink-0"
          />
          <div>
            <h3 className="text-[16px] font-medium text-foreground">Firza Miftahul Ilmi</h3>
            <p className="text-[13.5px] text-muted-foreground mt-1 leading-relaxed">
              Full-Stack Developer & AI Researcher focused on Informatics, systematic data curation pipelines, and modern TypeScript architectures.
            </p>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <aside className="mt-16 pt-12 border-t border-border/60">
            <h3 className="font-mono text-[11px] tracking-[0.2em] uppercase text-primary mb-6">
              Related Field Notes
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/articles/${rel.slug}`}
                  className="group p-5 rounded-xl border border-border/70 bg-card/30 hover:bg-card/60 hover:border-primary/40 transition-all flex flex-col justify-between"
                >
                  <div>
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-primary">
                      {rel.category}
                    </span>
                    <h4 className="mt-2 text-lg font-medium text-foreground group-hover:text-primary transition-colors leading-snug">
                      {rel.title}
                    </h4>
                    <p className="mt-2 text-[13.5px] text-foreground/70 line-clamp-2 leading-relaxed">
                      {rel.excerpt}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border/40 font-mono text-[10.5px] text-muted-foreground flex items-center justify-between">
                    <span>{rel.date}</span>
                    <span>{rel.readMin} min read</span>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        )}

      </div>
    </article>
  );
}
